/* useLiveConversationUpdates — SSE glue for the MessagesList page.
 *
 * Extracted from MessagesList.jsx to keep the page under the 200-line
 * React ceiling and to isolate the tricky real-time bookkeeping (typing
 * timers, debounced refetch, instant in-memory patching) from the JSX.
 *
 * Behaviour:
 *   • `message:new`     → instant in-memory patch of the affected row
 *                         (moves to top, updates preview, bumps unread)
 *                         + trailing debounced full refetch.
 *   • `conversation:new` / `conversation:read` → debounced refetch only.
 *   • `conversation:typing` (from someone else) → toggle the row's
 *                         "typing" state; auto-clears after 5 s so a
 *                         lost `is_typing=false` frame can't leave dots
 *                         stuck on the card indefinitely.
 *
 * Returns: `{ typingByConv }` — Map<convId, true> for the ConversationRow
 * to render animated dots on.
 */
import { useEffect, useRef, useState } from "react";
import {
  invalidateConversations,
  patchConversationOnMessage,
} from "../../lib/conversationsStore";
import { useMessagingStream } from "../../context/MessagingStreamContext";

const SSE_REFRESH_DEBOUNCE_MS = 500;
const TYPING_MAX_MS = 5000;

export function useLiveConversationUpdates(myId) {
  const [typingByConv, setTypingByConv] = useState({});
  const debouncedRef = useRef(null);
  const typingTimersRef = useRef({});

  const clearTypingFor = (cid) => {
    if (typingTimersRef.current[cid]) {
      clearTimeout(typingTimersRef.current[cid]);
      delete typingTimersRef.current[cid];
    }
    setTypingByConv((prev) => {
      if (!prev[cid]) return prev;
      const next = { ...prev }; delete next[cid]; return next;
    });
  };

  const scheduleRefetch = () => {
    if (debouncedRef.current) clearTimeout(debouncedRef.current);
    debouncedRef.current = setTimeout(invalidateConversations, SSE_REFRESH_DEBOUNCE_MS);
  };

  useMessagingStream((ev) => {
    if (!ev) return;
    if (ev.type === "message:new") {
      patchConversationOnMessage({ myId, event: ev });
      if (ev.conversation_id) clearTypingFor(ev.conversation_id);
      scheduleRefetch();
    } else if (ev.type === "conversation:new" || ev.type === "conversation:read") {
      scheduleRefetch();
    } else if (ev.type === "conversation:typing" && ev.user_id !== myId) {
      const cid = ev.conversation_id;
      if (!cid) return;
      if (typingTimersRef.current[cid]) clearTimeout(typingTimersRef.current[cid]);
      if (ev.is_typing) {
        setTypingByConv((prev) => (prev[cid] ? prev : { ...prev, [cid]: true }));
        typingTimersRef.current[cid] = setTimeout(() => clearTypingFor(cid), TYPING_MAX_MS);
      } else {
        clearTypingFor(cid);
      }
    }
  });

  useEffect(() => {
    return () => {
      if (debouncedRef.current) clearTimeout(debouncedRef.current);
      for (const t of Object.values(typingTimersRef.current)) clearTimeout(t);
      typingTimersRef.current = {};
    };
  }, []);

  return { typingByConv };
}
